// ============================================================
// LinkedCasesSection.tsx
// Redesign (final sections pass): drawn in the case shell (SELF_FRAMED) —
// section head with "Link a case", the same-accident AccidentGroupPanel,
// linked cases as card rows (name · client · reference · open in new tab ·
// Unlink, locked rows for cases the viewer can't open) and the shared
// section footer. LOGIC UNCHANGED (link via LinkCasePickerModal, unlink
// behind ConfirmDialogue, flush + re-fetch after either, open in the same
// view / new tab, useCaseAccess locked treatment, markComplete).
//
// REAL case-to-case linking (replaces the old read-only stub). Shows
// the case's firm-wide links as cards — each with the linked case's
// name (caseName || client name), its client + matter ref, and an
// Unlink button (behind a ConfirmDialogue). A "+ Link a case" button
// opens the firm-wide LinkCasePickerModal. Links are read from the
// case's `caseLinks` array (populated by getCaseById); after a
// link/unlink the current case is re-fetched so the list updates.
// ============================================================
import { refusalText } from '../../utils/serverError'
import { flushAutoSave } from '../../hooks/useAutoSave'
import React, { useState } from 'react'
import { useCasevoStore } from '../../store/useCasevoStore'
import { navigateTo } from '../../navigation/navigate'
import { linkCaseApi, unlinkCaseApi } from '../../api/casevoApi'
import type { CaseLinkSummary, CaseLinkRelationship } from '../../types/case.types'
import ModalFrame from '../shared/ModalFrame'
import { BTN_PRIMARY_MD } from '../../styles/buttons'
import { formatDateUK } from '../../utils/caseHelpers'
import { caseName, caseRef } from '../../utils/caseName'
import LinkCasePickerModal from '../shared/LinkCasePickerModal'
import ConfirmDialogue from '../shared/ConfirmDialogue'
import { LockIcon, LOCKED_CASE_TIP } from '../shared/CaseLink'
import { useCaseAccess } from '../../hooks/useCaseAccess'
import AccidentGroupPanel from '../cases/AccidentGroupPanel'
import { LinkRelationshipControl, RELATIONSHIPS } from '../cases/LinkRelationship'
import { useLoadPhase, SKELETON_BAR } from '../../hooks/useLoadPhase'
import { CARD, BTN_SEC, SM } from '../clientInfo/ClientInfoSections'
import { KIcon, SectionHead, SectionFooter } from '../case/sections/sectionKit'

// Display name for a linked case — the shared caseName() rule.
const linkDisplayName = (l: CaseLinkSummary): string => caseName(l)
const linkClientName = (l: CaseLinkSummary): string =>
  `${l.clientForename} ${l.clientSurname}`.trim() || 'Unnamed client'

// Round 2 — how two linked cases are connected (chosen when linking, shown on
// both cases; SAME_ACCIDENT also joins the accident group). The badge +
// choices are the shared LinkRelationshipControl (also on the client page).

const LinkedCasesSection: React.FC = () => {
  const { currentCase } = useCasevoStore()
  const [pickerOpen, setPickerOpen] = useState(false)
  const [linkBusy, setLinkBusy] = useState(false)
  const [linkError, setLinkError] = useState<string | null>(null)
  // The link pending unlink-confirmation (null = no confirm open).
  const [unlinkTarget, setUnlinkTarget] = useState<CaseLinkSummary | null>(null)
  const [unlinkBusy, setUnlinkBusy] = useState(false)
  const [unlinkError, setUnlinkError] = useState<string | null>(null)
  // Round 2 — the case picked in the picker, waiting for its relationship.
  const [pendingTarget, setPendingTarget] = useState<string | null>(null)
  const [pendingRel, setPendingRel] = useState<CaseLinkRelationship>('RELATED')
  // ★ NO LAYOUT SHIFT: the same-accident panel (its own fetch) sits ABOVE the
  // Linked list and used to pop in, pushing the list down. The list now waits
  // for it (same-shape skeleton after 250ms, invisible before) and both
  // appear together.
  const [groupReadyFor, setGroupReadyFor] = useState<string | null>(null)

  // Locked-link UX: which linked cases can this viewer open? (batched + cached)
  // Called BEFORE the early return (rules of hooks).
  const accessOf = useCaseAccess((currentCase?.caseLinks ?? []).map((l) => l.caseId))

  const groupPhase = useLoadPhase(!!currentCase && groupReadyFor === currentCase.id)
  if (!currentCase) return null
  const links = currentCase.caseLinks ?? []

  // Re-fetch the current case so its caseLinks reflect the latest state.
  // flushAutoSave first: the reload replaces currentCase with the server copy,
  // which would discard an edit typed in the last debounce second.
  const refresh = async (): Promise<void> => {
    await flushAutoSave()
    await useCasevoStore.getState().setCurrentCaseById(currentCase.id)
  }

  // Open a linked case in the SAME view. Mirrors the app's open flow
  // (handleOpenCase / DuplicateCaseDialog): push the current view to
  // history so Back returns here, clear the dashboard gate defensively
  // (already false inside a detail), then swap currentCase to the linked
  // one — setCurrentCaseById re-fetches + remaps, so ViewingPane simply
  // re-renders for the new case (no takeover panel is open in a detail).
  const openLinkedCase = async (caseId: string): Promise<void> => {
    await flushAutoSave() // persist this case's pending edit before switching
    // One history step (Back returns to this case); the current case stays
    // on screen until the linked one lands.
    await navigateTo({ view: 'caseDetail', caseId })
  }
  // Open a linked case in a NEW tab — same ?caseId mechanism the case
  // context menu uses (shared localStorage auth + App's on-load parse).
  const openLinkedCaseNewTab = (caseId: string): void => {
    window.open(`${window.location.origin}/?caseId=${caseId}`, '_blank')
  }

  // Picking a case asks HOW they're connected before linking (Round 2).
  const handlePick = (targetCaseId: string): void => {
    setLinkError(null)
    setPendingRel('RELATED')
    setPendingTarget(targetCaseId)
    setPickerOpen(false)
  }
  const handleLink = async (): Promise<void> => {
    if (!pendingTarget) return
    setLinkBusy(true)
    setLinkError(null)
    try {
      await linkCaseApi(currentCase.id, pendingTarget, pendingRel)
      await refresh()
      setPendingTarget(null)
    } catch (err) {
      // Surface the backend message (e.g. 409 already-linked) inline.
      setLinkError(err instanceof Error ? err.message : 'Failed to link case')
    } finally {
      setLinkBusy(false)
    }
  }
  const handleConfirmUnlink = async (): Promise<void> => {
    if (!unlinkTarget) return
    setUnlinkBusy(true)
    setUnlinkError(null)
    try {
      await unlinkCaseApi(currentCase.id, unlinkTarget.caseId)
      await refresh()
      setUnlinkTarget(null)
    } catch (err) {
      // The list stays as it is; the reason shows on it.
      setUnlinkTarget(null)
      setUnlinkError(refusalText(err, 'Couldn’t unlink that case. Please try again.'))
    } finally {
      setUnlinkBusy(false)
    }
  }

  return (
    <div className="text-[14px] leading-[1.5] text-casevo-forest">
      <SectionHead
        title="Linked cases"
        sub="Other cases connected to this one: co-claimants, the same accident, related matters."
        actions={
          <button type="button" className={BTN_SEC} onClick={() => { setLinkError(null); setPickerOpen(true) }}>
            <KIcon name="link" size={15} />Link a case
          </button>
        }
      />
      {/* (Batch 4) Same-accident group (confirmed) + heuristic suggestions to
          confirm / dismiss. Renders nothing when there's neither. Keyed by
          case so switching cases never shows the previous case's group. */}
      {/* Keyed on the links' relationships too, so choosing SAME_ACCIDENT (which
          joins the accident group) reloads the panel. */}
      <AccidentGroupPanel key={`${currentCase.id}:${links.map((l) => `${l.caseId}=${l.relationship ?? ''}`).join(',')}`} caseId={currentCase.id} onOpenCase={(id) => { void openLinkedCase(id) }}
        onLoaded={() => setGroupReadyFor(currentCase.id)} />

      {groupPhase === 'ready' ? (<>

      <div className="flex items-baseline gap-[10px] mt-[4px] mb-2 mx-[2px] text-[12px] font-bold tracking-[0.14em] uppercase text-casevo-text-soft">
        <h3 className="m-0 text-inherit font-inherit">Linked</h3>
        <span className="ml-auto tracking-normal normal-case text-[12.5px] font-semibold text-casevo-forest">
          {links.length === 0 ? 'None yet' : `${links.length} ${links.length === 1 ? 'case' : 'cases'}`}
        </span>
      </div>
      {links.length === 0 ? (
        <div className={`${CARD} px-6 py-9 text-center`}>
          <div className="w-12 h-12 rounded-[13px] bg-casevo-mint text-casevo-brand dark:text-[#a8e6c5] grid place-items-center mx-auto mb-3"><KIcon name="link" size={22} /></div>
          <h4 className="m-0 text-casevo-forest font-semibold text-[15px] mb-1">No linked cases yet</h4>
          <p className="m-0 mx-auto max-w-[420px] text-[13px] text-casevo-text-soft">
            Connect this case to any other case in your firm, e.g. co-defendants or the same accident.
          </p>
          <button type="button" className={`${BTN_SEC} mt-4`} onClick={() => { setLinkError(null); setPickerOpen(true) }}>
            <KIcon name="plus" size={15} />Link a case
          </button>
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {links.map((l) => {
            // ★ LOCKED: the viewer can't access the linked case → a muted,
            // non-clickable card with a lock (no navigate-then-error). Unlink
            // stays available (it edits THIS case, which they can access).
            const locked = accessOf(l.caseId) === 'locked'
            return (
              // Card BODY is click-to-open (same view). role=button +
              // keyboard; the inner action buttons stopPropagation. A
              // clickable <div> (not <button>) because it CONTAINS buttons.
              <div
                key={l.caseId}
                role={locked ? undefined : 'button'}
                tabIndex={locked ? undefined : 0}
                title={locked ? LOCKED_CASE_TIP : undefined}
                onClick={locked ? undefined : () => void openLinkedCase(l.caseId)}
                onKeyDown={locked ? undefined : (e) => {
                  // Only the card itself — Enter/Space on the inner Unlink /
                  // new-tab buttons must not ALSO open the case.
                  if (e.target !== e.currentTarget) return
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    void openLinkedCase(l.caseId)
                  }
                }}
                className={`${CARD} flex items-center gap-[14px] px-[14px] py-[12px] min-w-0 transition-colors ${locked
                  ? 'opacity-[0.62] cursor-not-allowed'
                  : 'cursor-pointer hover:border-casevo-mid focus:outline-none casevo-focus'}`}
              >
                <span className={`w-10 h-10 rounded-[11px] grid place-items-center shrink-0 ${locked ? 'bg-casevo-border-soft text-casevo-text-soft' : 'bg-casevo-mint text-casevo-brand dark:text-[#a8e6c5]'}`} aria-hidden="true">
                  <KIcon name={locked ? 'lock' : 'link'} size={18} />
                </span>
                <div className="min-w-0 flex-1">
                  <p className={`m-0 text-[14.5px] font-semibold truncate flex items-center gap-1.5 ${locked ? 'text-casevo-text-soft' : 'text-casevo-forest'}`}>
                    {locked && <LockIcon />}{linkDisplayName(l)}
                  </p>
                  {/* Locked: the server redacts the client fields, so this
                      line would read "Unnamed client · DRAFT-…" — omit it. */}
                  {!locked && (
                    <p className="m-0 text-[12.5px] text-casevo-text-soft truncate mt-px">
                      {linkClientName(l)} · <span className="font-mono text-[11.5px] tracking-[0.03em]">{caseRef({ ...l, id: l.caseId })}</span>
                    </p>
                  )}
                  {locked && <p className="m-0 text-[11.5px] text-casevo-text-soft">{LOCKED_CASE_TIP}</p>}
                  {/* Round 2 — the link's relationship (shared control; saving
                      publishes the change, then re-fetch for the shared accident). */}
                  <div className="mt-1.5">
                    <LinkRelationshipControl caseId={currentCase.id} link={l} locked={locked} onChanged={refresh} />
                  </div>
                  {l.relationship === 'SAME_ACCIDENT' && l.relationshipConfirmed !== false && l.sharedAccident && (l.sharedAccident.date || l.sharedAccident.place) && (
                    <p className="m-0 mt-1 text-[12.5px] text-casevo-forest">
                      <span className="text-casevo-text-soft">Accident: </span>
                      {l.sharedAccident.date ? <b className="font-semibold">{formatDateUK(l.sharedAccident.date)}</b> : <span className="text-casevo-text-soft">date not set</span>}
                      {l.sharedAccident.place && <> · {l.sharedAccident.place}</>}
                      <span className="text-casevo-text-soft"> · from {l.sharedAccident.fromCaseId === currentCase.id ? 'this case' : linkDisplayName(l)}</span>
                    </p>
                  )}
                </div>
                <div className="shrink-0 flex items-center gap-[2px]">
                  {/* Open in new tab — hidden for a locked (inaccessible) case. */}
                  {!locked && (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); openLinkedCaseNewTab(l.caseId) }}
                      aria-label={`Open ${linkDisplayName(l)} in a new tab`}
                      title="Open in new tab"
                      className="w-[30px] h-[30px] rounded-[8px] grid place-items-center text-casevo-text-soft hover:bg-casevo-mint hover:text-casevo-brand dark:hover:text-[#a8e6c5] focus:outline-none casevo-focus"
                    >
                      <KIcon name="open" size={15} />
                    </button>
                  )}
                  {/* Unlink — opens the ConfirmDialogue below. */}
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setUnlinkTarget(l) }}
                    aria-label={`Unlink ${linkDisplayName(l)}`}
                    className={`${BTN_SEC} ${SM} hover:!text-casevo-danger`}
                  >
                    <KIcon name="unlink" size={13} />Unlink
                  </button>
                </div>
              </div>
            )
          })}
        </div>
      )}
      </>) : (
        <div aria-hidden="true" className={groupPhase === 'wait' ? 'invisible' : ''}>
          <div className="flex items-baseline gap-[10px] mt-[4px] mb-2 mx-[2px] text-[12px] font-bold tracking-[0.14em] uppercase text-casevo-text-soft"><h3 className="m-0 text-inherit font-inherit">Linked</h3></div>
          <div className="flex flex-col gap-2">
            {Array.from({ length: Math.max(1, links.length) }, (_, i) => (
              <div key={i} className={`${CARD} flex items-center gap-[14px] px-[14px] py-[12px]`}>
                <span className="w-10 h-10 rounded-[11px] bg-casevo-border-soft motion-safe:animate-pulse shrink-0" />
                <div className="min-w-0 flex-1">
                  <span className="block h-[21.75px] leading-[21.75px]"><span className={`${SKELETON_BAR} h-3 w-48`} /></span>
                  <span className="block h-[18.75px] leading-[18.75px]"><span className={`${SKELETON_BAR} h-2.5 w-32`} /></span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <SectionFooter sectionKey="linkedCases" errors={[]} readySub="No required links: a case with no linked cases can still be completed." />

      {/* Firm-wide case picker */}
      <LinkCasePickerModal
        isOpen={pickerOpen}
        onClose={() => setPickerOpen(false)}
        currentCaseId={currentCase.id}
        excludeCaseIds={links.map((l) => l.caseId)}
        onPick={(targetCaseId) => handlePick(targetCaseId)}
        busy={linkBusy}
        error={linkError}
      />

      {/* Round 2 — how are the two cases connected? (asked on every new link) */}
      <ModalFrame
        isOpen={pendingTarget !== null}
        onClose={() => { if (!linkBusy) setPendingTarget(null) }}
        dismissible={!linkBusy}
        size="sm"
        eyebrow="Link a case"
        title="How are these cases connected?"
        subtitle="Shown on both cases. You can change it later."
        footerLeft={<button type="button" onClick={() => setPendingTarget(null)} disabled={linkBusy} className={`${BTN_SEC} ${SM}`}>Cancel</button>}
        footerRight={<button type="button" onClick={() => { void handleLink() }} disabled={linkBusy} className={BTN_PRIMARY_MD}>{linkBusy ? 'Linking…' : 'Link cases'}</button>}
      >
        <div role="radiogroup" aria-label="Relationship" className="flex flex-col gap-2">
          {RELATIONSHIPS.map((r) => (
            <label key={r.key} className={`flex items-start gap-3 rounded-[10px] border px-3 py-2.5 cursor-pointer ${pendingRel === r.key ? 'border-casevo-mid bg-casevo-mint/40' : 'border-casevo-border hover:border-casevo-mid'}`}>
              <input type="radio" name="link-relationship" value={r.key} checked={pendingRel === r.key} onChange={() => setPendingRel(r.key)} className="mt-1 accent-[#2d6a4f]" />
              <span>
                <span className="block text-[14px] font-semibold text-casevo-forest">{r.label}</span>
                <span className="block text-[12.5px] text-casevo-text-soft">{r.desc}</span>
              </span>
            </label>
          ))}
        </div>
        {linkError && <p role="alert" className="mt-3 text-[12.5px] text-casevo-danger">{linkError}</p>}
      </ModalFrame>

      {unlinkError && <p role="alert" className="mt-3 text-[12.5px] text-casevo-danger">{unlinkError}</p>}
      {/* Unlink confirmation */}
      <ConfirmDialogue
        isOpen={!!unlinkTarget}
        title="Unlink case"
        message={
          unlinkTarget
            ? `Remove the link to ${linkDisplayName(unlinkTarget)}? The other case won't be deleted — only the connection is removed.`
            : ''
        }
        confirmLabel={unlinkBusy ? 'Unlinking…' : 'Unlink'}
        onConfirm={() => void handleConfirmUnlink()}
        onCancel={() => setUnlinkTarget(null)}
      />
    </div>
  )
}
export default LinkedCasesSection
